import { InfoCircleOutlined } from "@ant-design/icons";
import {
  Alert,
  Card,
  Col,
  ConfigProvider,
  Row,
  Statistic,
  Tooltip,
  Typography,
} from "antd";
import dayjs from "dayjs";
import React from "react";
import { BAND_CSS_COLORS, consumptionBand } from "../lib/consumption-bands";
import { FleetSummary } from "../lib/types";

const money = (v: number) =>
  v.toLocaleString("en-US", { maximumFractionDigits: 2 });

interface SummaryCardsProps {
  summary?: FleetSummary;
  loading: boolean;
  activeThreshold?: number | null;
  onThresholdClick?: (threshold: number) => void;
}

// 13 cards, so 8 per row on a wide screen keeps the block to 2 rows and the
// Shipcos table above the fold. Card size="small" halves the body padding
// (antd bodyPaddingSM is a flat 12px against bodyPadding's paddingLG of 24).
const cardColProps = { xs: 24, sm: 12, md: 8, lg: 6, xl: 3 } as const;
const CARD_GUTTER: [number, number] = [12, 12];

// A Card sizes to its own content, so at this column width the titles that
// wrap to a second line ("Total AVCS Spend (USD)", "Average Vessel Spend")
// made their cards taller than their neighbours and left ragged bottoms.
// Row is a flex container, so its Cols already stretch to the tallest card on
// each wrapped line; height 100% is what makes the Card fill the Col it sits
// in. Content stays top aligned.
const CARD_STYLE: React.CSSProperties = { height: "100%" };

// Statistic has no size prop; its value size is the component token
// contentFontSize, which defaults to fontSizeHeading3 (24px). Scoped to this
// block on purpose: the app-wide ConfigProvider in App.tsx would shrink every
// Statistic in emr-web.
const compactStatistic = {
  components: { Statistic: { contentFontSize: 20 } },
};

// ADP and ENP are invoiced in GBP, so their totals are converted by the api.
// The rate and its date are named here so a stale rate is visible on the card
// rather than silently applied.
const conversionNote = (summary?: FleetSummary): string => {
  if (summary?.gbpUsdRate == null) {
    return "ADP and ENP are invoiced in GBP. No exchange rate is available, so this total cannot be converted to USD.";
  }
  const asOf = summary.gbpUsdRateAsOf
    ? ` recorded ${dayjs(summary.gbpUsdRateAsOf).format("D MMM YYYY")}`
    : "";
  return `ADP and ENP are invoiced in GBP. Converted at 1 GBP = ${summary.gbpUsdRate.toFixed(
    4
  )} USD${asOf}.`;
};

// "12 invoice lines", "1 invoice line".
const invoiceLinesText = (count: number): string =>
  `${count.toLocaleString("en-US")} invoice ${count === 1 ? "line" : "lines"}`;

// The api counts two kinds of invoice line it cannot price cleanly: lines
// with no published price, and lines invoiced in a currency other than the
// vessel's flat fee currency. They are said once, in grey, under the cards
// (ruling 2026-10-30), each part only when its count is above zero. Null when
// both are zero or missing.
const dataQualityText = (summary?: FleetSummary): string | null => {
  const noPriceLines = summary?.nullPriceLineCount ?? 0;
  const otherCurrencyLines = summary?.currencyMismatchLineCount ?? 0;
  const parts: string[] = [];
  if (noPriceLines > 0) {
    parts.push(`${invoiceLinesText(noPriceLines)} skipped (no price).`);
  }
  if (otherCurrencyLines > 0) {
    parts.push(`${invoiceLinesText(otherCurrencyLines)} in another currency.`);
  }
  return parts.length > 0 ? parts.join(" ") : null;
};

export const SummaryCards: React.FC<SummaryCardsProps> = ({
  summary,
  loading,
  activeThreshold = null,
  onThresholdClick,
}) => {
  const pct = summary?.consumptionPct ?? null;
  const dataQualityNote = dataQualityText(summary);

  const renderConvertedCard = (title: string, value: number | null) => (
    <Col {...cardColProps}>
      <Card size="small" loading={loading} style={CARD_STYLE}>
        <Statistic
          title={
            <Tooltip title={conversionNote(summary)}>
              <span>
                {title} <InfoCircleOutlined />
              </span>
            </Tooltip>
          }
          value={value == null ? "N/A" : money(value)}
        />
      </Card>
    </Col>
  );

  // With a handler the 3 threshold cards act as filter toggles for the
  // table below; without one (no consumer wired) they stay plain cards.
  const renderThresholdCard = (threshold: number, count: number) => {
    const active = activeThreshold === threshold;
    const clickable = onThresholdClick !== undefined;
    return (
      <Col {...cardColProps}>
        <Card
          size="small"
          loading={loading}
          hoverable={clickable}
          onClick={clickable ? () => onThresholdClick(threshold) : undefined}
          {...(clickable ? { role: "button", "aria-pressed": active } : {})}
          style={
            active
              ? { ...CARD_STYLE, borderColor: "#1677ff", borderWidth: 2 }
              : CARD_STYLE
          }
        >
          <Statistic title={`Vessels >= ${threshold}%`} value={count} />
        </Card>
      </Col>
    );
  };

  return (
    <ConfigProvider theme={compactStatistic}>
      <Row gutter={CARD_GUTTER}>
        <Col {...cardColProps}>
          <Card size="small" loading={loading} style={CARD_STYLE}>
            <Statistic
              title="Total AVCS Spend (USD)"
              value={money(summary?.totalAvcsSpend ?? 0)}
            />
          </Card>
        </Col>
        {renderConvertedCard(
          "Total ADP Spend (USD)",
          summary?.totalAdpSpend ?? null
        )}
        {renderConvertedCard(
          "Total ENP Spend (USD)",
          summary?.totalEnpSpend ?? null
        )}
        <Col {...cardColProps}>
          <Card size="small" loading={loading} style={CARD_STYLE}>
            <Statistic
              title={
                <Tooltip title="Counts AVCS only until ADP and ENP join the flat fee scope.">
                  <span>
                    Total Spend (USD) <InfoCircleOutlined />
                  </span>
                </Tooltip>
              }
              value={money(summary?.totalSpend ?? 0)}
            />
          </Card>
        </Col>
        <Col {...cardColProps}>
          <Card size="small" loading={loading} style={CARD_STYLE}>
            <Statistic
              title="Total Budget"
              value={money(summary?.totalBudget ?? 0)}
            />
          </Card>
        </Col>
        <Col {...cardColProps}>
          <Card size="small" loading={loading} style={CARD_STYLE}>
            <Statistic
              title="Consumption %"
              value={pct === null ? "N/A" : pct}
              suffix={pct === null ? undefined : "%"}
              valueStyle={
                pct === null
                  ? undefined
                  : { color: BAND_CSS_COLORS[consumptionBand(pct)] }
              }
            />
          </Card>
        </Col>
        {renderThresholdCard(50, summary?.vesselsOver50 ?? 0)}
        {renderThresholdCard(75, summary?.vesselsOver75 ?? 0)}
        {renderThresholdCard(100, summary?.vesselsOver100 ?? 0)}
        <Col {...cardColProps}>
          <Card size="small" loading={loading} style={CARD_STYLE}>
            <Statistic
              title="Fee Pending"
              value={summary?.vesselsPendingBudget ?? 0}
            />
          </Card>
        </Col>
        <Col {...cardColProps}>
          <Card size="small" loading={loading} style={CARD_STYLE}>
            <Statistic title="Total Vessels" value={summary?.vesselCount ?? 0} />
          </Card>
        </Col>
        <Col {...cardColProps}>
          <Card size="small" loading={loading} style={CARD_STYLE}>
            <Statistic
              title={
                <Tooltip title="Total Spend divided by the number of budgeted vessels. Vessels with a pending fee are not included.">
                  <span>
                    Average Vessel Spend <InfoCircleOutlined />
                  </span>
                </Tooltip>
              }
              value={
                summary?.averageVesselSpend == null
                  ? "N/A"
                  : money(summary.averageVesselSpend)
              }
            />
          </Card>
        </Col>
        <Col {...cardColProps}>
          <Card size="small" loading={loading} style={CARD_STYLE}>
            <Statistic
              title="Latest Invoice Date"
              value={
                summary?.latestInvoiceDate
                  ? dayjs(summary.latestInvoiceDate).format("D MMM YYYY")
                  : "N/A"
              }
            />
          </Card>
        </Col>
      </Row>
      {dataQualityNote && (
        <Typography.Paragraph
          type="secondary"
          style={{ marginTop: 8, marginBottom: 0 }}
        >
          {dataQualityNote}
        </Typography.Paragraph>
      )}
      {summary && summary.unattributedSpend > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 16 }}
          message={
            <Tooltip title="Invoice spend in the selected period that could not be matched to a flat fee vessel. The IMO number is unknown or the vessel has no AVCS flat fee subscription. This spend is not included in any vessel or shipco totals.">
              <span>
                Unattributed flat fee spend:{" "}
                {money(summary.unattributedSpend)} <InfoCircleOutlined />
              </span>
            </Tooltip>
          }
        />
      )}
    </ConfigProvider>
  );
};
